import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Bot, User, Loader2, AlertCircle, Plus, ChevronDown, Paperclip, X, FileText, Image as ImageIcon,
} from 'lucide-react';
import { agentsService } from '@/services/agents.service';
import { conversationsService } from '@/services/conversations.service';
import { taskPipelineService } from '@/services/taskPipeline.service';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface MessageAttachment {
  filename: string;
  type: string;
  size: number;
  previewUrl?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  attachments?: MessageAttachment[];
}

interface CollectedData {
  [key: string]: string | number | boolean | null;
}

interface TestSession {
  conversation_id: string;
  session_id: string;
  status: string;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
}

interface AgentTestChatProps {
  agentId: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatSessionLabel = (s: TestSession) => {
  const d = new Date(s.last_message_at || s.created_at);
  const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${dateStr}, ${timeStr} · ${s.message_count} msgs`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const AgentTestChat: React.FC<AgentTestChatProps> = ({ agentId, className }) => {
  // Messages
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collectedData, setCollectedData] = useState<CollectedData>({});

  // Extraction polling
  const [isPollingExtraction, setIsPollingExtraction] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track mounted state to prevent state updates after unmount
  const mountedRef = useRef(true);

  // Attachments
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sessions
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cleanup polling on unmount and track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowSessionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load sessions
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await agentsService.getTestSessions(agentId);
      setSessions(data);
    } catch {
      // silent
    } finally {
      setIsLoadingSessions(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load a specific session's messages
  const loadSessionMessages = async (session: TestSession) => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setIsPollingExtraction(false);
    setIsLoadingHistory(true);
    setMessages([]);
    setCollectedData({});
    setError(null);
    setSessionId(session.session_id);
    setConversationId(session.conversation_id);
    setShowSessionDropdown(false);

    try {
      const msgs = await agentsService.getConversationMessages(session.conversation_id);
      setMessages(
        msgs.map((m) => ({
          role: (m.sender_type === 'customer' ? 'user' : 'assistant') as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(m.created_at),
        })),
      );
    } catch {
      setError('Failed to load conversation');
    } finally {
      setIsLoadingHistory(false);
      inputRef.current?.focus();
    }
  };

  // New chat
  const handleNewChat = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setIsPollingExtraction(false);
    const newId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    setSessionId(newId);
    setConversationId(null);
    setMessages([]);
    setCollectedData({});
    setError(null);
    setPendingFiles([]);
    setShowSessionDropdown(false);
    inputRef.current?.focus();
  };

  // File handling
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // Poll for extraction follow-up message
  const startExtractionPolling = useCallback((convId: string, knownMessageCount: number) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    setIsPollingExtraction(true);
    const startTime = Date.now();
    const maxDuration = 30_000; // 30 seconds

    pollingRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        return;
      }

      try {
        // Timeout: stop polling after 30 seconds
        if (Date.now() - startTime > maxDuration) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsPollingExtraction(false);
          setIsLoading(false);
          setError('Image processing timed out. The response may appear when you send the next message.');
          return;
        }

        const msgs = await conversationsService.getMessages(convId);
        if (!mountedRef.current) return;

        // Look for new agent messages beyond what we already know about
        const agentMessages = msgs.filter(m => m.sender_type === 'agent' || m.sender_type === 'human');
        if (agentMessages.length > knownMessageCount) {
          // Found new agent message(s) — take the latest one
          const latestAgentMsg = agentMessages[agentMessages.length - 1];
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsPollingExtraction(false);
          setIsLoading(false);

          const responseContent = latestAgentMsg.content;
          if (responseContent && responseContent.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: responseContent,
                timestamp: new Date(latestAgentMsg.created_at),
              },
            ]);
          }

          inputRef.current?.focus();
        }
      } catch {
        // Polling error — silently continue, will timeout eventually
      }
    }, 2000);
  }, []);

  // Send message
  const handleSendMessage = async () => {
    if ((!inputValue.trim() && pendingFiles.length === 0) || isLoading) return;

    const activeSessionId =
      sessionId || Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    if (!sessionId) setSessionId(activeSessionId);

    const filesToSend = [...pendingFiles];
    const messageAttachments: MessageAttachment[] = filesToSend.map(f => ({
      filename: f.name,
      type: f.type,
      size: f.size,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }));

    const userMessage: Message = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
      attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setPendingFiles([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setIsLoading(true);
    setError(null);

    let enteredPollingPath = false;

    try {
      const history = messages.map((msg) => ({
        content: msg.content,
        sender_type: msg.role === 'user' ? 'customer' : 'agent',
        content_type: 'text' as const,
      }));

      // Build attachment metadata for AI context
      const attachmentMeta = filesToSend.length > 0
        ? filesToSend.map(f => ({ filename: f.name, type: f.type || 'file', size: f.size }))
        : undefined;

      const response = await agentsService.testAgent(agentId, {
        message: userMessage.content || (filesToSend.length > 0 ? '(sent a file)' : ''),
        history,
        collected_data: collectedData,
        session_id: activeSessionId,
        attachments: attachmentMeta,
      });

      // Upload files to the conversation via existing attachment API
      const convId = response.conversation_id || conversationId;
      if (convId && filesToSend.length > 0) {
        for (const file of filesToSend) {
          try {
            await taskPipelineService.uploadAttachment(convId, file);
          } catch {
            // Upload failed but message was sent — non-blocking
          }
        }
      }

      if (response.collected_data) setCollectedData(response.collected_data as CollectedData);
      if (response.conversation_id) setConversationId(response.conversation_id);

      if (!mountedRef.current) return;

      // If extraction is pending, poll for the follow-up AI response
      if (response.pending_extraction && convId) {
        // Count existing agent messages to detect when a new one arrives
        const currentAgentMsgs = await conversationsService.getMessages(convId);
        if (!mountedRef.current) return;
        const agentMsgCount = currentAgentMsgs.filter(
          m => m.sender_type === 'agent' || m.sender_type === 'human'
        ).length;
        startExtractionPolling(convId, agentMsgCount);
        enteredPollingPath = true;
        // Keep isLoading true — the typing indicator stays visible
      } else {
        // Normal flow: show the response immediately
        const responseMessage = response.message;
        if (responseMessage && responseMessage.trim()) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: responseMessage,
              timestamp: new Date(),
            },
          ]);
        } else {
          // Empty response — show a fallback so the user knows something happened
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'Agent is processing your request...',
              timestamp: new Date(),
            },
          ]);
        }
        setIsLoading(false);
      }

      loadSessions();
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
    } finally {
      if (mountedRef.current && !enteredPollingPath) {
        setIsLoading(false);
      }
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-grow textarea to fit content
  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  // Current session label for dropdown button
  const currentSession = sessions.find((s) => s.session_id === sessionId);
  const currentLabel = currentSession
    ? formatSessionLabel(currentSession)
    : sessionId
      ? 'New conversation'
      : 'Select a session';

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className={cn('flex flex-col h-full', className)}>

      {/* ---- Session bar ---- */}
      <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-[#1a1a2e] bg-white" ref={dropdownRef}>
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setShowSessionDropdown(!showSessionDropdown)}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-left min-w-0"
          >
            <span className="text-xs text-gray-600 truncate flex-1">{currentLabel}</span>
            <ChevronDown className={cn('w-3 h-3 text-gray-400 flex-shrink-0 transition-transform', showSessionDropdown && 'rotate-180')} />
          </button>

          {/* Dropdown */}
          {showSessionDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] z-20 max-h-60 overflow-y-auto">
              {isLoadingSessions ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No sessions yet</p>
              ) : (
                sessions.map((session) => {
                  const isActive = session.session_id === sessionId;
                  return (
                    <button
                      key={session.conversation_id}
                      onClick={() => loadSessionMessages(session)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 text-xs transition-colors border-b border-gray-50 last:border-0',
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50',
                      )}
                    >
                      <span className="block truncate font-medium">
                        {session.session_id === 'default'
                          ? 'Default Session'
                          : `Session ${session.session_id.slice(0, 8)}`}
                      </span>
                      <span className="block text-[11px] text-gray-400 mt-0.5">
                        {formatSessionLabel(session)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleNewChat}
          className="flex-shrink-0 p-1.5 rounded-xl bg-blue-500 text-white hover:bg-blue-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
          title="New conversation"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ---- Messages ---- */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Bot className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Start a conversation</p>
              <p className="text-[11px] text-gray-300 mt-1">Type a message below to begin</p>
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn(
                'flex gap-2.5 items-end',
                msg.role === 'user' ? 'flex-row-reverse' : 'flex-row',
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                  msg.role === 'user' ? 'bg-blue-500' : 'bg-gray-200',
                )}
              >
                {msg.role === 'user' ? (
                  <User className="w-3 h-3 text-white" />
                ) : (
                  <Bot className="w-3 h-3 text-gray-500" />
                )}
              </div>
              <div
                className={cn(
                  'max-w-[80%] flex flex-col',
                  msg.role === 'user' ? 'items-end' : 'items-start',
                )}
              >
                <div
                  className={cn(
                    'rounded-xl px-3.5 py-2 text-[13px] leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-md border-2 border-[#1a1a2e]'
                      : 'bg-white text-gray-800 border-2 border-[#1a1a2e] rounded-bl-md shadow-[3px_3px_0px_#1a1a2e]',
                  )}
                >
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-col gap-1.5 mb-1.5">
                      {msg.attachments.map((att, ai) => (
                        <div key={ai} className={cn(
                          'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs',
                          msg.role === 'user' ? 'bg-blue-400/30' : 'bg-gray-50 border border-gray-200',
                        )}>
                          {att.type.startsWith('image/') ? (
                            att.previewUrl ? (
                              <img src={att.previewUrl} alt={att.filename} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                            ) : (
                              <ImageIcon className="w-4 h-4 flex-shrink-0 opacity-70" />
                            )
                          ) : (
                            <FileText className="w-4 h-4 flex-shrink-0 opacity-70" />
                          )}
                          <span className="truncate">{att.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.content && <p className="whitespace-pre-wrap break-words">{msg.content}</p>}
                </div>
                <span className="text-[10px] text-gray-300 mt-1 px-1">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            </div>
          ))
        )}

        {/* Typing indicator */}
        {isLoading && (
          <div className="flex gap-2.5 items-end">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              <Bot className="w-3 h-3 text-gray-500" />
            </div>
            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl rounded-bl-md px-4 py-2.5 shadow-[3px_3px_0px_#1a1a2e]">
              {isPollingExtraction && (
                <p className="text-[11px] text-gray-400 mb-1">Processing image...</p>
              )}
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-xl border-2 border-[#1a1a2e] text-xs">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ---- Input ---- */}
      <div className="border-t-2 border-[#1a1a2e] bg-white px-3 py-3">
        {/* Pending files preview */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {pendingFiles.map((file, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-600 max-w-[180px]">
                {file.type.startsWith('image/') ? (
                  <ImageIcon className="w-3 h-3 flex-shrink-0 text-blue-500" />
                ) : (
                  <FileText className="w-3 h-3 flex-shrink-0 text-orange-500" />
                )}
                <span className="truncate">{file.name}</span>
                <span className="text-gray-400 flex-shrink-0">({formatFileSize(file.size)})</span>
                <button onClick={() => removePendingFile(i)} className="flex-shrink-0 text-gray-400 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
            multiple
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isLoadingHistory}
            className="flex-shrink-0 w-9 h-9 rounded-xl bg-gray-50 text-gray-500 flex items-center justify-center hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-40 border-2 border-[#1a1a2e]"
            title="Attach file"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyDown}
            placeholder={pendingFiles.length > 0 ? 'Add a message or send files...' : 'Type a message...'}
            disabled={isLoading || isLoadingHistory}
            rows={1}
            className="flex-1 min-h-[36px] max-h-[120px] px-3 py-2 bg-gray-50 border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none transition-colors disabled:opacity-50 resize-none"
          />
          <button
            onClick={handleSendMessage}
            disabled={(!inputValue.trim() && pendingFiles.length === 0) || isLoading || isLoadingHistory}
            className="flex-shrink-0 w-9 h-9 rounded-xl bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-40 disabled:hover:bg-blue-500 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
